//src/mis/components/TableRejection.jsx
import * as React from 'react';
import { Button, Box, Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Search, Refresh } from '@mui/icons-material';
import {
  GridRowModes,
  DataGrid,
  GridToolbarExport, 
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridCellModes,
} from '@mui/x-data-grid';
import {
  randomCreatedDate,
  randomTraderName,
  randomId,
  randomArrayItem,
} from '@mui/x-data-grid-generator';

import { useLocation, useNavigate } from "react-router-dom";
import { ReportOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useState } from "react";
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import RejectionAdd from "../pages/RejectionAdd";
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import RejectionEdit from '../pages/RejectionEdit';
import env from "react-dotenv";
import moment from "moment";

import {
  createWorkbook,
  createWorksheet,
  addTitleRow,
  addSummaryRow,
  addHeaders,
  applyCellStyles,
  setColumnWidths
} from '../../utils/excelStyles'; // Adjust the path as necessary

import { saveAs } from 'file-saver'; // Ensure you have this import
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  BASE_PRODUCT_FIELDS,
  collectDynamicProductNames,
  isAllowedProduct,
  isBaseProduct,
  normalizeItemKey,
  resolveItemValue,
  resolveRowProductValue,
  sanitizeToColumn,
} from '../utils/misProductColumns';

const initialRows = [
  
];

// Normalize to consistent YearMonth key 'MMM-YYYY' from various inputs
function normalizeYearMonth(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const strictFormats = [
    'MMM-YYYY','MMM yyyy','MMM-YY','MMM YY',
    'YYYY-MM','YYYY/MM',
    'YYYY-MM-DD','YYYY/MM/DD',
    'DD-MM-YYYY','DD/MM/YYYY'
  ];
  for (const fmt of strictFormats) {
    const m = moment(raw, fmt, true);
    if (m.isValid()) return m.format('MMM-YYYY');
  }
  const lenientFormats = ['MMM-YYYY','MMM YYYY','MMM-YY','MMM YY'];
  for (const fmt of lenientFormats) {
    const m = moment(raw, fmt, false);
    if (m.isValid()) return m.format('MMM-YYYY');
  }
  const mAny = moment(raw);
  if (mAny.isValid()) return mAny.format('MMM-YYYY');
  return raw;
}

// Resolve aggregate map for a row YearMonth against pivot keys (handles MMM-YYYY, YYYY-MM, and loose matches)
function resolveAggForYM(rawYm, pivot) {
  if (!rawYm || !pivot) return {};
  const ym = normalizeYearMonth(rawYm);
  const isoYm = moment(rawYm, [
    'YYYY-MM-DD','YYYY/MM/DD','YYYY-MM','YYYY/MM','MMM-YYYY','MMM YYYY','MMM-YY','MMM YY'
  ], true).isValid() ? moment(rawYm).format('YYYY-MM') : moment(ym,'MMM-YYYY').format('YYYY-MM');
  if (pivot[ym]) return pivot[ym];
  if (pivot[isoYm]) return pivot[isoYm];
  const target = moment(ym, 'MMM-YYYY');
  if (!target.isValid()) return {};
  let found = {};
  for (const k of Object.keys(pivot)) {
    const mk = moment(k, ['MMM-YYYY','MMM YYYY','YYYY-MM','YYYY/MM'], false);
    if (mk.isValid() && mk.year() === target.year() && mk.month() === target.month()) {
      found = pivot[k];
      break;
    }
  }
  return found || {};
}

function EditToolbar(props) {
  const { setRows, setRowModesModel } = props;
  const navigate = useNavigate();
  const { selectedCellParams, cellMode, cellModesModel, setCellModesModel, handleExport, data, setFilteredData, handleRefresh} = props;
  const [searchQuery, setSearchQuery] = useState('');
  const user = useSelector((state) => state.user);
  // const [isAdmin, setIsAdmin] = useState(user?.role === 'Supply Admin' ? true : false);
  const [isAdmin, setIsAdmin] = useState(() => {
      if (!user || !user.department) return false;
      const role = user.role?.toLowerCase();
      const department = user.department?.toLowerCase();
      return (
        (role === 'admin' && department.includes('supply')) || 
        (role === 'super admin' && department.includes('supply'))
      );
    });

  const [buttonPopup, setButtonPopup] = useState(false);

  const handleSearch = (query) => {
        setSearchQuery(query);
        const filtered = data.filter((row) => {
            return Object.values(row).some((field) =>
                field && field.toString().toLowerCase().includes(query.toLowerCase())
            );
        });
        setFilteredData(filtered);
    };

  const handleSaveOrEdit = () => {
    if (!selectedCellParams) {
      return;
    }
    const { id, field } = selectedCellParams;
    
    if (cellMode === 'edit') {      
        navigate(`../editEfficiency/${id}`, { replace: true })
    } else {
        navigate(`../editEfficiency/${id}`, { replace: true })
    }
  };

  const handleDelete = async () => {
    
    if (!selectedCellParams) {
        return;
      }
      const { id, field, dataId } = selectedCellParams;
      const rIndex = dataId;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_MISBACKEND_URL}/deleteRejectionRecord/${rIndex}`,
        {
          method: "DELETE",
        }
      );
      
      navigate("../efficiencyHome/4");   
      navigate(0);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    if (!selectedCellParams) {
      return;
    }
    const { id, field } = selectedCellParams;
    setCellModesModel({
      ...cellModesModel,
      [id]: {
        ...cellModesModel[id],
        [field]: { mode: GridCellModes.View, ignoreModifications: true },
      },
    });
  };

  const handleMouseDown = (event) => {
    // Keep the focus in the cell
    event.preventDefault();
  };

  const handleClick = () => {
    // const id = randomId();
    // setRows((oldRows) => [...oldRows, { id, name: '', age: '', isNew: true }]);
    // setRowModesModel((oldModel) => ({
    //   ...oldModel,
    //   [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' },
    // }));
  };
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  return (
   
    <GridToolbarContainer>
      {/*
       {isAdmin && ( 
        //    <Button color="primary" startIcon={<AddIcon />} href="../efficiencyHome/add" variant="outlined">
        //    Add
        //  </Button> 
           <div>
                   <Button disabled onClick={()=>setOpen(true)}
                    >Add
                </Button>
                <RejectionAdd open={open} onClose={() => setOpen(false)}/>
            </div>
        )} 
    
       {isAdmin && 
        (  
          <>
           <Button color="primary" startIcon={<EditIcon />}
          onClick={()=>{setOpenEdit(true)}}
          onMouseDown={handleMouseDown}
          disabled={!selectedCellParams}
          >
          Edit
          </Button> 
          <RejectionEdit onSelected={selectedCellParams} open={openEdit} onClose={() => setOpenEdit(false)}/> 
          </>
            
         )
        } 
        
         {isAdmin && false && (  <Button color="primary" startIcon={<DeleteIcon />}
          onClick={handleDelete}
          onMouseDown={handleMouseDown}
          disabled={!selectedCellParams}
          variant="outlined"
        >
          {cellMode === 'edit' ? 'Delete' : 'Delete'}
        </Button>
        )} 
        */}

        <Grid item>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)} // Call the search function
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: '1150px', // Set the desired width here
                marginTop: "10px",                  
                marginBottom: "10px", 
                marginLeft: "2px",
                marginRight: "2px",
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#00308F',
                    },
                    '&:hover fieldset': {
                      borderColor: '#00207E',
                    },
                  },
                }}
            />
          </Grid>
        
        {/* <GridToolbarExport/> */}

        <Button
        color="primary"
        onClick={handleExport}
        startIcon={<SaveAltIcon />}
        >
          Export
        </Button>

        <Grid item>
          <IconButton
            onClick={() => window.location.reload()} // Refresh the page
            color="primary"
            sx={{
              backgroundColor: '#ffffff',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
          >
            <Refresh />
          </IconButton>
          </Grid>
    
    </GridToolbarContainer>
  );
}
const FullFeaturedEficiency = () => {

  const [selectedCellParams, setSelectedCellParams] = React.useState(null);
  const [cellModesModel, setCellModesModel] = React.useState({});
  const user = useSelector((state) => state.user);
  // const [isAdmin, setIsAdmin] = useState(user?.role === 'admin' ? true : false);
  const [isAdmin, setIsAdmin] = useState(() => {
      if (!user || !user.department) return false;
      const role = user.role?.toLowerCase();
      const department = user.department?.toLowerCase();
      return (
        (role === 'admin' && department.includes('supply')) || 
        (role === 'super admin' && department.includes('supply'))
      );
    });

    const handleSearch = (query) => {
        setSearchQuery(query);
        const filtered = rows.filter((row) => {
            return Object.values(row).some((field) =>
                field && field.toString().toLowerCase().includes(query.toLowerCase())
            );
        });
        setFilteredData(filtered);
    };
    const handleRefresh = () => {
        setSearchQuery('');
        getRowsFromDB(); // Call the function to fetch data from the server
    };

  const handleCellFocus = React.useCallback((event) => {
    const row = event.currentTarget.parentElement;
    const dataId = row.dataset.id;
    const id = row.dataset.rowindex;
    const field = event.currentTarget.dataset.field;
    setSelectedCellParams({ id, field,dataId });
  }, []);

  const cellMode = React.useMemo(() => {
    if (!selectedCellParams) {
      return 'view';
    }
    const { id, field } = selectedCellParams;
    return cellModesModel[id]?.[field]?.mode || 'view';
  }, [cellModesModel, selectedCellParams]);

  const handleCellKeyDown = React.useCallback(
    (params, event) => {
      if (cellMode === 'edit') {
        // Prevents calling event.preventDefault() if Tab is pressed on a cell in edit mode
        event.defaultMuiPrevented = true;
      }
    },
    [cellMode],
  );

  const handleCellEditStop = React.useCallback((params, event) => {
    event.defaultMuiPrevented = true;
  }, []);


  const [searchQuery, setSearchQuery] = useState('');
  const [rows, setRows] = useState(initialRows); // Initialize with initialRows
  const [filteredData, setFilteredData] = useState(rows); // Initialize with the original rows
  const [supplyRows, setSupplyRows] = React.useState(initialRows);
  const [warrantyRows, setWarrantyRows] = React.useState(initialRows);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [obj , setObj] = React.useState({ABPB:""});
  const [complaintItems, setComplaintItems] = React.useState([]);
  const [complaintPivot, setComplaintPivot] = React.useState({}); // { 'MMM-YYYY': { itemName: sumQty } }
  const [mainProductsSet, setMainProductsSet] = React.useState(null); // normalized allowed items
  const [supplyTotalsByYm, setSupplyTotalsByYm] = React.useState({}); // { 'MMM-YYYY': totalSupply }
  const navigate = useNavigate();


  const location = useLocation();
  React.useEffect( () => {
    if(location.key === undefined || location.key === null || location.key === 'default'){
      navigate("/");   
    }
  }, [ location ] );
 
  const getRowsFromDB = async () => {
    try 
    {
      const maintenanceResponse = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/rejection`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
     
      });
      const records = await maintenanceResponse.json();
     
      // setRows(Object.keys(records).map((key) => records[key])); 
      const processedData = Object.keys(records).map((key) => records[key]); 
      const sortedData = processedData.sort((a, b) => {
      return new Date(b.YearMonth) - new Date(a.YearMonth); // Sort in descending order
    });
      // setRows(processedData);
      // setFilteredData(processedData); // Initialize filtered data  
      setRows(sortedData);
      setFilteredData(sortedData); // Initialize filtered data    
    
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
        getRowsFromDB();
    }, []);

  // Fetch complaints pivot (server-side) and attach as dynamic columns
  React.useEffect(() => {
    const fetchComplaints = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/complaints/pivot`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        setComplaintItems(Array.isArray(data?.items) ? data.items : []);
        setComplaintPivot(data?.pivot || {});
      } catch (e) {
        // ignore fetch errors
      }
    };
    fetchComplaints();
  }, []);

  // Fetch allowed items from main_product and keep as a normalized Set
  React.useEffect(() => {
    const fetchMainProducts = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_PJCBACKEND_URL}/api/main-product`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        const names = new Set();
        const pushName = (v) => {
          const n = normalizeItemKey(v);
          if (n) names.add(n);
        };
        if (Array.isArray(data)) {
          const candidateKeys = ['product','Product','product_name','ProductName','item','Item','item_name','ItemName','name','Name'];
          data.forEach((row) => {
            if (typeof row === 'string') {
              pushName(row);
              return;
            }
            if (!row || typeof row !== 'object') return;
            for (const key of candidateKeys) {
              const val = row[key];
              if (typeof val === 'string' && val.trim()) {
                pushName(val);
              }
            }
          });
        }
        setMainProductsSet(names);
      } catch (e) {
        setMainProductsSet(new Set());
      }
    };
    fetchMainProducts();
  }, []);

  // Fetch monthly supply totals to compute dynamic percentages
  React.useEffect(() => {
    const fetchSupplyTotals = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/supply`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        const map = {};
        Object.values(data || {}).forEach((row) => {
          if (!row) return;
          const ym = normalizeYearMonth(row.YearMonth);
          let total = 0;
          BASE_PRODUCT_FIELDS.forEach((f) => {
            const v = Number(row[f]);
            if (Number.isFinite(v)) total += v;
          });
          Object.keys(row).forEach((key) => {
            if (isBaseProduct(key)) return;
            const v = Number(row[key]);
            if (Number.isFinite(v)) total += v;
          });
          if (!map[ym]) map[ym] = 0;
          map[ym] += total;
          // Also store ISO key to maximize lookup success
          const iso = moment(ym, 'MMM-YYYY', true).isValid() ? moment(ym, 'MMM-YYYY').format('YYYY-MM') : '';
          if (iso) {
            if (!map[iso]) map[iso] = 0;
            map[iso] += total;
          }
        });
        setSupplyTotalsByYm(map);
      } catch (e) {
        setSupplyTotalsByYm({});
      }
    };
    fetchSupplyTotals();
  }, []);

  // Enrich visible rows with dynamic values from pivot for easier rendering/search
  React.useEffect(() => {
    if (!rows || !rows.length) return;
    const enriched = rows.map((row) => {
      const rawYm = row?.YearMonth;
      const agg = resolveAggForYM(rawYm, complaintPivot);
      const added = {};
      Object.entries(agg).forEach(([name, qty]) => {
        if (isBaseProduct(name)) return;
        if (!isAllowedProduct(name, mainProductsSet)) return;
        const field = sanitizeToColumn(name);
        if (typeof qty === 'number') {
          added[field] = qty;
          added[name] = qty;
        }
      });
      return { ...row, ...added };
    });
    const combined = [...enriched];
    combined.sort((a, b) => {
      const ma = moment(normalizeYearMonth(a?.YearMonth), 'MMM-YYYY');
      const mb = moment(normalizeYearMonth(b?.YearMonth), 'MMM-YYYY');
      return mb.valueOf() - ma.valueOf();
    });
    setFilteredData(combined);
  }, [complaintPivot, rows, mainProductsSet]);

  /* reverted dynamic complaints */

  const getInsertRowsInDB = async () => {
    try 
    {
      const maintenanceResponse = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/rejectionTable`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
     
      });
      const records = await maintenanceResponse.json();
      getRowsFromDB();  
    
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    getInsertRowsInDB();
  }, []);


//   const getSupplyRowsFromDB = async () => {
//     try 
//     {
//       const maintenanceResponse = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/supply`, {
//       method: "GET",
//       headers: { "Content-Type": "application/json" },
     
//       });
//       const records = await maintenanceResponse.json();
     
//       setSupplyRows(Object.keys(records).map((key) => records[key]));      
    
//     } catch (error) {
//       console.log(error);
//     }
//   };

//   React.useEffect(() => {
//     getSupplyRowsFromDB();
//   }, []);


//  const getWarrantyRowsFromDB = async () => {
//     try 
//     {
//       const maintenanceResponse = await fetch(`${process.env.REACT_APP_MISBACKEND_URL}/warranty`, {
//       method: "GET",
//       headers: { "Content-Type": "application/json" },
     
//       });
//       const records = await maintenanceResponse.json();
     
//       setWarrantyRows(Object.keys(records).map((key) => records[key]));      
    
//     } catch (error) {
//       console.log(error);
//     }
//   };

//   React.useEffect(() => {
//     getWarrantyRowsFromDB();
//   }, []);

  
//     const calculateRejectionPercentage = (supply, warranty) => {
//       return supply.map((supplyItem) => {
//        const warrantyItem = warranty.find(item => item.YearMonth === supplyItem.YearMonth) || {};
//        const { 
//           ABPB: supplyAbpb, 
//           BJRL: supplyBjrl ,
//           BJTL: supplybjtl ,
//           PBS: supplyPbs ,
//           RPPS: supplyRpps,
//           RPLBS: supplyRplbs,
//           LBS: supplyLbs, 
//           TCEJ: supplyTcej, 
//           RS: supplyRs, 
//           MEP: supplyMep,
//         } = supplyItem;
//         const { 
//           ABPB: warrantyAbpb =0,
//           BJRL: warrantyBjrl =0,
//           BJTL: warrantybjtl  =0,
//           PBS: warrantyPbs  =0,
//           RPPS: warrantyRpps =0 ,
//           RPLBS: warrantyRplbs=0,
//           LBS: warrantyLbs=0, 
//           TCEJ: warrantyTcej=0, 
//           RS: warrantyRs=0, 
//           MEP: warrantyMep=0, 
//         } = warrantyItem;

//         const abpbRejectionPercentage = (supplyAbpb === "" || supplyAbpb === null || supplyAbpb === 'undefined' 
//                                         || supplyAbpb === 0 || supplyAbpb === "0" || isNaN(supplyAbpb))
//                                         ?  0  
//                                         : (warrantyAbpb === "" || warrantyAbpb === null 
//                                         || warrantyAbpb === 'undefined' || warrantyAbpb === 0 || isNaN(warrantyAbpb)
//                                         || warrantyAbpb === "0")
//                                         ? 0
//                                         : (parseInt(warrantyAbpb) / parseInt(supplyAbpb)) * 100 ;
        
//         const bjrlRejectionPercentage = (supplyBjrl === "" || supplyBjrl === null || supplyBjrl === 'undefined' 
//                                         || supplyBjrl === 0 || isNaN(supplyBjrl) || supplyBjrl === "0")
//                                         ?  0  
//                                         : (warrantyBjrl === "" || warrantyBjrl === null || warrantyBjrl === 'undefined'
//                                            || warrantyBjrl === 0 || isNaN(warrantyBjrl) || warrantyBjrl === "0")
//                                         ? 0
//                                         : (parseInt(warrantyBjrl) / parseInt(supplyBjrl)) * 100 ;

//         const bjtlRejectionPercentage = (supplybjtl === "" || supplybjtl === null || supplybjtl === 'undefined'
//                                        || supplybjtl === 0 || isNaN(supplybjtl) || supplybjtl === "0")
//                                         ?  0  
//                                         : (warrantybjtl === "" || warrantybjtl === null || warrantybjtl === 'undefined' 
//                                         || warrantybjtl === 0 || isNaN(warrantybjtl) || warrantybjtl === "0")
//                                         ? 0
//                                         : (parseInt(warrantybjtl) / parseInt(supplybjtl)) * 100 ;

//         const pbsRejectionPercentage = (supplyPbs === "" || supplyPbs === null || supplyPbs === 'undefined' 
//                                         || supplyPbs === 0 || isNaN(supplyPbs) || supplyPbs === "0")
//                                         ?  0  
//                                         : (warrantyPbs === "" || warrantyPbs === null || warrantyPbs === 'undefined' 
//                                         || warrantyPbs === 0 || isNaN(warrantyPbs)|| warrantyPbs === "0" )
//                                         ? 0
//                                         : (parseInt(warrantyPbs) / parseInt(supplyPbs)) * 100 ;
                                        
        
//         const rppsRejectionPercentage = (supplyRpps === "" || supplyRpps === null || supplyRpps === 'undefined'
//                                          || supplyRpps === 0 || isNaN(supplyRpps) || supplyRpps === "0" )
//                                         ?  0  
//                                         :(warrantyRpps === "" || warrantyRpps === null 
//                                         || warrantyRpps === 'undefined' || warrantyRpps === 0 
//                                         || isNaN(warrantyRpps) || warrantyRpps === "0" )
//                                         ? 0
//                                         : (parseInt(warrantyRpps) / parseInt(supplyRpps)) * 100 ;

//         const rplbsRejectionPercentage = (supplyRplbs === "" || supplyRplbs === null || supplyRplbs === 'undefined' 
//                                         || supplyRplbs === 0 || isNaN(supplyRplbs) || supplyRplbs === "0")
//                                         ?  0  
//                                         :(warrantyRplbs === "" || warrantyRplbs === null || warrantyRplbs === 'undefined'
//                                          || warrantyRplbs === 0 || isNaN(warrantyRplbs) || warrantyRplbs === "0")
//                                         ? 0
//                                         : (parseInt(warrantyRplbs) / parseInt(supplyRplbs)) * 100 ;

//         const lbsRejectionPercentage = (supplyLbs === "" || supplyLbs === null || supplyLbs === 'undefined' 
//                                         || supplyLbs === 0 || isNaN(supplyLbs) || supplyLbs === "0")
//                                         ?  0  
//                                         :(warrantyLbs === "" || warrantyLbs === null || warrantyLbs === 'undefined' 
//                                         || warrantyLbs === 0 || isNaN(warrantyLbs) || warrantyLbs === "0")
//                                         ? 0
//                                         : (parseInt(warrantyLbs) / parseInt(supplyLbs)) * 100 ;

//         const tcejRejectionPercentage = (supplyTcej === "" || supplyTcej === null || supplyTcej === 'undefined' 
//                                         || supplyTcej === 0 || isNaN(supplyTcej) || supplyTcej === "0")
//                                         ?  0  
//                                         :(warrantyTcej === "" || warrantyTcej === null || warrantyTcej === 'undefined'
//                                          || warrantyTcej === 0 || isNaN(warrantyTcej) || warrantyTcej === "0")
//                                         ? 0
//                                         : (parseInt(warrantyTcej) / parseInt(supplyTcej)) * 100 ;

//         const rsRejectionPercentage = (supplyRs === "" || supplyRs === null || supplyRs === 'undefined' 
//                                         || supplyRs === 0 || isNaN(supplyRs) || supplyRs === "0")
//                                         ?  0  
//                                         : (warrantyRs === "" || warrantyRs === null || warrantyRs === 'undefined'
//                                          || warrantyRs === 0 || isNaN(warrantyRs) || warrantyRs === "0")
//                                         ? 0
//                                         : (parseInt(warrantyRs) / parseInt(supplyRs)) * 100 ;

//         const mepRejectionPercentage = (supplyMep === "" || supplyMep === null || supplyMep === 'undefined' 
//                                         || supplyMep === 0 || isNaN(supplyMep)|| supplyMep === "0") 
//                                         ?  0  
//                                         :(warrantyMep === "" || warrantyMep === null || warrantyMep === 'undefined' || warrantyMep === 0
//                                          || isNaN(warrantyMep) || warrantyMep === "0")
//                                         ? 0
//                                         : (parseInt(warrantyMep) / parseInt(supplyMep)) * 100 ;
         
//         return {
//           id: supplyItem.id,
//           YearMonth: supplyItem.YearMonth,
//           ABPB : abpbRejectionPercentage.toFixed(2),
//           BJRL : bjrlRejectionPercentage.toFixed(2),
//           BJTL : bjtlRejectionPercentage.toFixed(2),
//           PBS : pbsRejectionPercentage.toFixed(2),
//           RPPS : rppsRejectionPercentage.toFixed(2),
//           RPLBS : rplbsRejectionPercentage.toFixed(2),
//           LBS : lbsRejectionPercentage.toFixed(2),
//           TCEJ : tcejRejectionPercentage.toFixed(2),
//           RS : rsRejectionPercentage.toFixed(2),
//           MEP : mepRejectionPercentage.toFixed(2),
//         };
//        });
//     };
    
//     const rejectionPercentages = calculateRejectionPercentage(supplyRows, warrantyRows);   

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const onRowsSelectionHandler = (ids) => {
    
    navigate(`../editEfficiency/${ids}`, { replace: true })
  };
  
  

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleExport = async () => {
    const workbook = createWorkbook();
    const worksheet = createWorksheet(workbook, 'Warranty % Data');

    addTitleRow(worksheet, 'Warranty % Data Report');
    addSummaryRow(worksheet, `Summary of Warranty % Data on ${new Date().toLocaleDateString()}`);

    const dynamicNames = collectDynamicProductNames(mainProductsSet, complaintItems, rows);
    const excelHeaders = ['S/N', 'YearMonth', ...BASE_PRODUCT_FIELDS, ...dynamicNames];
    addHeaders(worksheet, excelHeaders);

    const sortedRows = [...rows].sort((a, b) => {
        return new Date(b.YearMonth) - new Date(a.YearMonth);
    });

    sortedRows.forEach((row, index) => {
      // Base fields are already percentages in rejection table
      const baseValues = [
        row.ABPB, row.BJRL, row.BJTL, row.PBS, row.RPPS,
        row.RPLBS, row.LBS, row.TCEJ, row.RS, row.MEP
      ];
      // Dynamic values as percentages using same logic as grid
      const rawYm = row.YearMonth;
      const agg = resolveAggForYM(rawYm, complaintPivot);
      const ym = normalizeYearMonth(rawYm);
      const iso = moment(ym, 'MMM-YYYY', true).isValid() ? moment(ym, 'MMM-YYYY').format('YYYY-MM') : '';
      const denom = supplyTotalsByYm[ym] ?? (iso ? supplyTotalsByYm[iso] : undefined);
      const dynamicValues = dynamicNames.map((name) => {
        const direct = resolveRowProductValue(row, name);
        if (direct != null && direct !== '') {
          const pct = Number(direct);
          return Number.isFinite(pct) ? pct.toFixed(2) : direct;
        }
        const resolved = resolveItemValue(agg, name);
        const qty = Number(resolved);
        if (!Number.isFinite(qty)) return '';
        if (Number.isFinite(denom) && denom > 0) {
          return ((qty / denom) * 100).toFixed(2);
        }
        return qty.toFixed(2);
      });
      worksheet.addRow([
        index + 1,
        row.YearMonth,
        ...baseValues,
        ...dynamicValues,
      ]);
    });

    applyCellStyles(worksheet);
    const widths = [5, 20, ...Array(excelHeaders.length - 2).fill(10)];
    setColumnWidths(worksheet, widths);

    // Ensure title (row 1) and subtitle (row 2) borders span all dynamic columns
    const totalCols = excelHeaders.length;
    const titleRow = worksheet.getRow(1);
    const subRow = worksheet.getRow(2);
    for (let c = 1; c <= totalCols; c++) {
      const tCell = titleRow.getCell(c);
      const sCell = subRow.getCell(c);
      tCell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: c === 1 ? { style: 'thin' } : undefined,
        right: c === totalCols ? { style: 'thin' } : undefined,
      };
      sCell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: c === 1 ? { style: 'thin' } : undefined,
        right: c === totalCols ? { style: 'thin' } : undefined,
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'Warranty % Data.xlsx');
};

  const columns = React.useMemo(() => {
    const baseColumns = [
      { field: 'YearMonth', headerName: 'YearMonth', width: 120 },
      { field: 'ABPB', headerName: 'ABPB', width: 80},
      { field: 'BJRL', headerName: 'BJRL', width: 80},
      { field: 'BJTL', headerName: 'BJTL', width: 80},
      { field: 'PBS', headerName: 'PBS', width: 80},
      { field: 'RPPS', headerName: 'RPPS', width: 80},
      { field: 'RPLBS', headerName: 'RPLBS', width: 80},
      { field: 'LBS', headerName: 'LBS', width: 80},
      { field: 'TCEJ', headerName: 'TCEJ', width: 80},
      { field: 'RS', headerName: 'RS', width: 80},
      { field: 'MEP', headerName: 'MEP', width: 80},
    ];
    const dynamicNames = collectDynamicProductNames(mainProductsSet, complaintItems, rows);
    const dynamicCols = dynamicNames.map((name) => {
        const field = sanitizeToColumn(name);
        return {
          field,
          headerName: name,
          width: 80,
          valueGetter: (params) => {
            const row = params?.row || {};
            const direct = resolveRowProductValue(row, name);
            if (direct != null && direct !== '') {
              const pct = Number(direct);
              if (Number.isFinite(pct)) return pct;
            }
            const rawYm = params?.row?.YearMonth;
            const agg = resolveAggForYM(rawYm, complaintPivot);
            const v = resolveItemValue(agg, name);
            const qty = Number(v);
            if (!Number.isFinite(qty)) return 0;
            const ym = normalizeYearMonth(rawYm);
            const iso = moment(ym, 'MMM-YYYY', true).isValid() ? moment(ym, 'MMM-YYYY').format('YYYY-MM') : '';
            const denom = supplyTotalsByYm[ym] ?? (iso ? supplyTotalsByYm[iso] : undefined);
            if (Number.isFinite(denom) && denom > 0) {
              return (qty / denom) * 100;
            }
            return qty;
          },
          renderCell: (params) => {
            const row = params?.row || {};
            const direct = resolveRowProductValue(row, name);
            if (direct != null && direct !== '') {
              const pct = Number(direct);
              if (Number.isFinite(pct)) return pct.toFixed(2);
            }
            const rawYm = params?.row?.YearMonth;
            const agg = resolveAggForYM(rawYm, complaintPivot);
            const resolved = resolveItemValue(agg, name);
            let qty = Number(resolved);
            if (!Number.isFinite(qty)) qty = 0;
            const ym = normalizeYearMonth(rawYm);
            const iso = moment(ym, 'MMM-YYYY', true).isValid() ? moment(ym, 'MMM-YYYY').format('YYYY-MM') : '';
            const denom = supplyTotalsByYm[ym] ?? (iso ? supplyTotalsByYm[iso] : undefined);
            const value = Number.isFinite(denom) && denom > 0 ? (qty / denom) * 100 : qty;
            return Number.isFinite(value) ? value.toFixed(2) : '0.00';
          },
        };
      });
    return [...baseColumns, ...dynamicCols];
  }, [complaintItems, complaintPivot, mainProductsSet, supplyTotalsByYm, rows]);

  

  return (
    <Box
      sx={{
        height: 600,
        width: '95%',
        marginLeft:"180px",
        paddingRight:"90px"
      }}
    > 
      <DataGrid
        //  rows={rows.slice(0).sort((a,b)=>
        //   moment(new Date(a.YearMonth)).format("YYYY-MM")
        //   .localeCompare(moment(new Date(b.YearMonth)).format("YYYY-MM"))).reverse()
        //   }
        rows={filteredData} // Use filteredData here
        columns={columns}
        checkboxSelection 
        disableMultipleRowSelection
        sx={{
            boxShadow: 1,
            border: 1,
            borderColor: 'primary.light',
            typography: 'subtitle2',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '.MuiDataGrid-columnHeaderTitle': { 
                font: 'light important',
                overflow: 'visible !important'
             }
          }}
        
        onCellKeyDown={handleCellKeyDown}
        cellModesModel={cellModesModel}
        onCellEditStop={handleCellEditStop}
        onCellModesModelChange={(model) => setCellModesModel(model)}
        slots={{
            toolbar: EditToolbar           
          }}
        slotProps={{
            toolbar: {
              cellMode,
              selectedCellParams,
              setSelectedCellParams,
              cellModesModel,
              setCellModesModel,
              handleExport, // Pass the export function here
              data: rows, // Pass the rows to the toolbar
              setFilteredData, // Pass the setter for filtered data
              handleRefresh, // Pass the refresh function
            },
            cell: {
              onFocus: handleCellFocus,
            },
        }}
        getRowId={(row) => row.id ?? row.YearMonth}
      />
    </Box>
  );
}
export default FullFeaturedEficiency;